import { ResponsiveImage } from "@components/atoms/ResponsiveImage"
import { FilmSearchResultItem } from "@features/program/useFilmSearch"
import { WishlistIcon } from "@features/wishlist/WishlistIcon"
import { EventType } from "@generated-types"
import { useIsMobile } from "@hooks/useIsMobile"
import { cn, tv } from "@utils/tw"
import dayjs from "dayjs"
import { Link } from "eddev/routing"
import { AnimatePresence, motion } from "motion/react"
import { ComponentProps, MutableRefObject, useEffect, useMemo, useRef, useState } from "react"
import { useRafLoop, useWindowSize } from "react-use"
import { FilmHoverCard } from "../../components/film/FilmHoverTile"
import { useProgramData } from "@features/program/useProgramData"

type CalendarModel = {
  dates: Record<string, CalendarDate>
  maxRowsShown: number
}

type CalendarDate = {
  dateKey: string
  startHour: dayjs.Dayjs
  endHour: dayjs.Dayjs
  rows: CalendarRow[]
}

type CalendarRow = {
  hideVenueName?: boolean
  venueName: string
  venueOrder?: number
  cinemaName: string
  cinemaNumber: number
  sessions: FilmSearchResultItem[]
}

export type CalendarNavigator = {
  goToDate: (date: string) => void
}

// Structure the session list into a hierarchical structure, avoiding later computation
function useCalendarModel(allSessions: FilmSearchResultItem[]) {
  const venueOrder = useProgramData().data?.currentProgram?.venues

  return useMemo((): CalendarModel => {
    const model: CalendarModel = {
      dates: {},
      maxRowsShown: 0,
    }
    const distinctVenues = new Set<string>()

    // Set up all the data by looping through the sessions
    for (const item of allSessions) {
      const eventType = item.film.eventInfo?.eventType
      if ((eventType !== EventType.Event && eventType !== EventType.Film) || !item.session?.info?.dateTime) continue
      const dateKey = dayjs(item.session?.info?.dateTime).format("YYYY-MM-DD")
      const startDate = dayjs(item.session?.info?.dateTime)
      const endDate = startDate.add(item.film.eventInfo?.runtime || 60, "minute")

      // Create the date model if it doesn't exist
      let dateModel = model.dates[dateKey]!
      if (!dateModel) {
        dateModel = model.dates[dateKey] = {
          dateKey,
          startHour: startDate,
          endHour: endDate,
          rows: [],
        }
      }

      // Extend the start/end hours
      dateModel.startHour = startDate.isBefore(dateModel.startHour) ? startDate : dateModel.startHour
      dateModel.endHour = endDate.isAfter(dateModel.endHour) ? endDate : dateModel.endHour

      dateModel.startHour = dateModel.startHour.startOf("hour")
      // dateModel.endHour = dateModel.endHour.add(1, "hour").startOf("hour")

      // Find the venue/cinema names
      const venueName = item.session?.info?.venue?.titleShort
      if (!venueName) continue
      const cinemaName = item.session?.info?.venue?.titleSub ?? ""

      distinctVenues.add(venueName + "" + cinemaName)

      // Ensure a row exists for the venue/cinema
      let row = dateModel.rows.find((row) => row.venueName === venueName && row.cinemaName === cinemaName)
      if (!row) {
        row = {
          venueName,
          venueOrder: venueOrder?.findIndex((venue) => venue?.parentSlug === item.session?.info?.venue?.parentSlug),
          cinemaName,
          cinemaNumber: parseInt(cinemaName.replace(/[^0-9]/g, "")) ?? 0,
          sessions: [],
        }
        dateModel.rows.push(row)
      }

      // Add the session to the row
      row.sessions.push(item)
    }

    // Ensure the date object is in the correct order
    model.dates = Object.fromEntries(Object.entries(model.dates).sort((a, b) => a[1].startHour.diff(b[1].startHour)))

    // Sort the venues and their sessions, and hide venue names for continuous venues
    for (const dateModel of Object.values(model.dates)) {
      // Sort the rows by venue name and then by cinema number
      dateModel.rows.sort((a, b) => {
        // if venueOrder === -1, sort by venueName instead
        if (a.venueOrder === -1) {
          return a.venueName.localeCompare(b.venueName)
        }
        if (b.venueOrder === -1) {
          return b.venueName.localeCompare(a.venueName)
        }
        // otherwise sort by cinemaNumber then by venueOrder
        if (a.venueName === b.venueName) {
          return a.cinemaNumber - b.cinemaNumber
        }
        return a.venueOrder! - b.venueOrder!
      })

      // Extend the start/end hours by 1 hour
      dateModel.startHour = dateModel.startHour.subtract(1, "hour")
      dateModel.endHour = dateModel.endHour.add(1, "hour")

      // Tidy up each row...
      let lastVenueName = ""
      for (const row of dateModel.rows) {
        // Hide the venue name for contiguous venues
        if (row.venueName === lastVenueName) {
          row.hideVenueName = true
        } else {
          row.hideVenueName = false
          lastVenueName = row.venueName
        }

        // Sort sessions by start time
        row.sessions.sort((a, b) => {
          return a.session?.info?.dateTime?.localeCompare(b.session?.info?.dateTime!)!
        })
      }

      model.maxRowsShown = Math.max(model.maxRowsShown, dateModel.rows.length)
    }

    return model
  }, [allSessions])
}

type BrowseCalendarProps = {
  items: FilmSearchResultItem[]
  className?: string
  navigatorRef?: MutableRefObject<CalendarNavigator>
  onDateChange?: (date: string) => void
}

export function BrowseCalendar(props: BrowseCalendarProps) {
  // Build the calendar model
  const model = useCalendarModel(props.items)

  const isMobile = useIsMobile(800)

  const outerRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const [enabled, setEnabled] = useState(true)
  const { width } = useWindowSize()
  const lastTick = useRef(0)
  const currentDay = useRef("")
  const days = useRef<NodeListOf<HTMLElement> | null>(null)
  const times = useRef<NodeListOf<HTMLElement> | null>(null)
  const sidebar = useRef<HTMLElement | null>(null)
  const dateLinks = useRef<NodeListOf<HTMLButtonElement> | null>(null)
  const daysPositions = useRef(
    [] as {
      x: number
      w: number
      h: number
      xLerp: number
      wLerp: number
      hLerp: number
    }[],
  )
  const [dayShown, setDayShown] = useState(Object.keys(model.dates)[0])

  const drag = useRef({
    down: false,
    dragged: false,
    scrolling: false,
    scrollingTimer: 0,
    start: {
      x: 0,
      y: 0,
    },
    delta: {
      x: 0,
      y: 0,
    },
    progress: {
      x: 0,
      y: 0,
    },
    current: {
      xLerp: 0,
      yLerp: 0,
    },
  })

  const pointerDown = (e: PointerEvent) => {
    if (e.target instanceof HTMLElement && ref.current?.contains(e.target)) {
      const d = drag.current
      d.start.x = e.clientX
      d.start.y = e.clientY
      d.down = true
      d.dragged = false
    }
  }

  const pointerMove = (e: PointerEvent) => {
    const d = drag.current
    if (!d.down) return
    d.delta.x = e.clientX - d.start.x
    d.delta.y = e.clientY - d.start.y

    if (Math.abs(d.delta.x) > 10) {
      d.dragged = true
    }
  }

  const pointerUp = () => {
    const d = drag.current
    if (!d.down) return
    d.down = false
    d.progress.x += d.delta.x
    d.progress.y += d.delta.y
    d.delta.x = 0
    d.delta.y = 0
  }

  const preventDefault = (e: Event) => {
    e.preventDefault()
  }

  const canClick = (e: Event) => {
    if (drag.current.dragged) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const onTouch = (e: TouchEvent) => {
    if (drag.current.dragged && drag.current.down) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  const onWheel = (e: WheelEvent) => {
    if (outerRef.current && outerRef.current.contains(e.target as Node)) {
      if (Math.abs(e.deltaX) >= 1) {
        drag.current.scrolling = true
        clearTimeout(drag.current.scrollingTimer)
        drag.current.scrollingTimer = window.setTimeout(() => {
          drag.current.scrolling = false
        }, 100)
      }

      if (drag.current.scrolling) {
        e.preventDefault()
        e.stopPropagation()
      }

      drag.current.progress.x -= e.deltaX
    }
  }

  useEffect(() => {
    days.current = outerRef.current?.querySelectorAll(".cal-day") || null
    times.current = outerRef.current?.querySelectorAll(".cal-day-time") || null
    sidebar.current = outerRef.current?.querySelector(".cal-sidebar") || null
    dateLinks.current = outerRef.current?.querySelectorAll(".cal-date") || null
    const links = ref.current?.querySelectorAll("a")

    if (days.current) {
      days.current.forEach((el, i) => {
        if (enabled) {
          el.style.setProperty("position", "absolute")
          el.style.setProperty("left", "0px")
          el.style.setProperty("top", "0px")
          el.style.setProperty("transition", "none")
          el.style.setProperty("user-select", "none")
        } else {
          el.style.removeProperty("position")
          el.style.removeProperty("left")
          el.style.removeProperty("top")
          el.style.removeProperty("transition")
          el.style.removeProperty("user-select")
        }

        daysPositions.current[i] = {
          x: 0,
          w: 0,
          h: 0,
          xLerp: 0,
          wLerp: 0,
          hLerp: 0,
        }
      })
    }

    if (links) {
      links.forEach((el) => {
        el.addEventListener("mousedown", preventDefault, false)
        el.addEventListener("mousemove", preventDefault, false)
        el.addEventListener("click", canClick, false)
      })
    }

    return () => {
      if (links) {
        links.forEach((el) => {
          el.removeEventListener("mousedown", preventDefault, false)
          el.removeEventListener("mousemove", preventDefault, false)
          el.removeEventListener("click", canClick, false)
        })
      }
    }
  }, [width, ref, enabled, model])

  const scrollToDay = (day: number) => {
    const pos = daysPositions.current[day]
    if (pos) {
      drag.current.progress.x = -pos.x + (sidebar.current!.offsetWidth - 1)
    }
  }

  const scrollToNow = () => {
    const dateIndex = Object.keys(model.dates).indexOf(dayjs().format("YYYY-MM-DD"))
    const dayPosition = daysPositions.current[dateIndex]
    const nowBar = ref.current?.querySelector("[data-now-bar]")
    console.log(dateIndex, dayPosition)
    if (nowBar) {
      drag.current.progress.x =
        -dayPosition.x +
        (sidebar.current!.offsetWidth - 1) -
        parseFloat(getComputedStyle(nowBar).left.replace("px", "")) +
        window.innerWidth * 0.2
    }
  }

  useEffect(() => {
    setTimeout(() => {
      scrollToNow()
    }, 1000)
  }, [model.dates])

  if (props.navigatorRef) {
    props.navigatorRef.current = {
      goToDate: (date: string) => {
        const day = Object.keys(model.dates).indexOf(date)
        if (day !== -1) {
          scrollToDay(day)
        }
      },
    }
  }

  useRafLoop(() => {
    if (!ref.current || !outerRef.current) return

    if (days.current && ref.current) {
      let x = 0
      let maxH = 0
      let maxW = 0
      let activeDay = 0

      days.current.forEach((el, i) => {
        const pos = daysPositions.current[i]
        const w = el.offsetWidth
        const h = el.offsetHeight

        pos.x = x
        pos.w = w
        pos.h = h

        x += pos.w
        maxH = i === 0 ? h : maxH
        maxW += w
      })

      // clamp scrolling
      drag.current.progress.x = Math.min(
        Math.max(drag.current.progress.x, -maxW + window.innerWidth / 2),
        sidebar.current!.offsetWidth - 1,
      )

      drag.current.current.xLerp += (drag.current.progress.x + drag.current.delta.x - drag.current.current.xLerp) * 0.2
      drag.current.current.yLerp += (drag.current.progress.y + drag.current.delta.y - drag.current.current.yLerp) * 0.2

      days.current.forEach((el, i) => {
        const timeEl = times.current![i]
        const pos = daysPositions.current[i]

        pos.wLerp += (pos.w - pos.wLerp) * 0.125
        pos.xLerp += (pos.x + drag.current.current.xLerp - pos.xLerp) * 0.2

        pos.wLerp = Math.round(pos.wLerp * 1000) / 1000
        pos.xLerp = Math.round(pos.xLerp * 1000) / 1000

        const inView = pos.xLerp + pos.wLerp > 0 && pos.xLerp < outerRef.current!.offsetWidth
        const isActive = pos.xLerp + pos.wLerp > 0 && pos.xLerp < outerRef.current!.offsetWidth - window.innerWidth / 2

        if (enabled && inView) {
          const dateKey = el.getAttribute("data-key")
          el.style.transform = `translateX(${pos.xLerp}px)`
          el.style.setProperty("visibility", "visible")
          timeEl.style.transform = `translateX(${pos.xLerp}px)`
          timeEl.style.setProperty("visibility", "visible")
          if (isActive) {
            if (currentDay.current !== dateKey) {
              setDayShown(el.getAttribute("data-key")!)
              props.onDateChange?.(el.getAttribute("data-key")!)
              currentDay.current = dateKey!
            }
            maxH = pos.h
            activeDay = i
          }
        } else {
          el.style.removeProperty("transform")
          el.style.setProperty("visibility", "hidden")
          timeEl.style.removeProperty("transform")
          timeEl.style.setProperty("visibility", "hidden")
        }
      })

      if (enabled) {
        ref.current.style.height = `${maxH}px`
      } else {
        ref.current.style.removeProperty("height")
      }
    }

    if (dateLinks.current) {
      dateLinks.current.forEach((el) => {
        if (el.getAttribute("data-key") === dayShown) {
          if (!el.classList.contains("active")) {
            el.classList.add("active")
            // el.scrollIntoView({ block: "center" })
          }
        } else {
          el.classList.remove("active")
        }
      })
    }

    lastTick.current = Date.now()
  })

  useEffect(() => {
    window.addEventListener("pointerdown", pointerDown, { passive: true })
    window.addEventListener("pointermove", pointerMove, { passive: true })
    window.addEventListener("pointerup", pointerUp, { passive: true })
    window.addEventListener("pointercancel", pointerUp, { passive: true })
    window.addEventListener("pointerleave", pointerUp, { passive: true })
    outerRef.current?.addEventListener("wheel", onWheel, { passive: false })
    outerRef.current?.addEventListener("touchmove", onTouch, false)

    return () => {
      window.removeEventListener("pointerdown", pointerDown)
      window.removeEventListener("pointermove", pointerMove)
      window.removeEventListener("pointerup", pointerUp)
      window.removeEventListener("pointercancel", pointerUp)
      window.removeEventListener("pointerleave", pointerUp)
      outerRef.current?.removeEventListener("wheel", onWheel)
      outerRef.current?.removeEventListener("touchmove", onTouch)
    }
  }, [])

  useEffect(() => {
    const filterBar = document.querySelectorAll(".results-browser-filter-bar")
    const ro = new ResizeObserver((entries) => {
      let h = 0
      entries.forEach((entry) => {
        if (entry.target instanceof HTMLElement) {
          h = Math.max(h, entry.borderBoxSize[0].blockSize)
        }
      })
      outerRef.current!.style.setProperty("--filter-bar-height", h + "px")
    })
    filterBar.forEach((el) => {
      ro.observe(el)
    })
    return () => {
      ro.disconnect()
    }
  })

  return (
    <div className="relative [--pixels-per-minute:2px] md:[--pixels-per-minute:3px]" ref={outerRef}>
      {/* Time bar up top */}
      <div className="sticky top-[calc(var(--spacing-headerHeight)+var(--filter-bar-height))] bg-bg z-[5] w-full overflow-hidden h-7 md:h-5 -mb-[1px]">
        {Object.entries(model.dates)?.map(([key, date]) => {
          return (
            <div
              className="cal-day-time absolute top-0 left-0 border-b border-b-bg-low"
              data-key={date.dateKey}
              key={key}
            >
              <TimelineHeader startTime={date.startHour} endTime={date.endHour} />
            </div>
          )
        })}
      </div>
      <div className={cn("w-full relative overflow-hidden min-h-[80vw]", props.className)}>
        <div ref={ref} className="w-full relative flex flex-nowrap">
          {/* Sidebar */}
          <VenuesSidebar model={model} dayShown={dayShown} />

          {/* Days */}
          {Object.entries(model.dates)?.map(([key, date]) => {
            return (
              <CalendarDay model={model} date={date} key={key} active={dayShown === date.dateKey} isMobile={isMobile} />
            )
          })}
        </div>
      </div>
    </div>
  )
}

function TimelineHeader(props: { startTime: dayjs.Dayjs; endTime: dayjs.Dayjs }) {
  const { endTime, startTime } = props
  const hoursBetween = endTime.add(60 - endTime.minute(), "minute").diff(startTime, "hour")

  const now = useTime()

  const isToday = now.isSame(startTime, "day")

  return useMemo(
    () => (
      <div className="flex flex-nowrap bg-bg h-7 md:h-5 border-b border-bg-mid">
        {/* Current Time Indicator */}
        {isToday && (
          <div
            className="absolute bottom-[-4px] size-[8px] overflow-hidden z-20"
            style={{ left: `calc(var(--pixels-per-minute)*${now.diff(startTime, "minute")} - 3.5px)` }}
          >
            <div className="size-[8px] bg-coral outline-coral rotate-45 translate-y-[-5px]" />
          </div>
        )}
        {Array.from({ length: hoursBetween - 1 }).map((_, i) => {
          const time = startTime.subtract(startTime.minute(), "minute").add(i, "hour")
          return (
            <div key={i} className="relative items-start justify-end h-full w-[calc(var(--pixels-per-minute)*60)]">
              <span className="type-label-caps pl-2">{time.format("ha")}</span>
              {Array.from({ length: 4 }).map((_, j) => {
                return (
                  <div
                    key={j}
                    className={cn(
                      "border-solid absolute bottom-0",
                      !(i == 0 && j == 0) && "border-l",
                      j === 0 ? "border-bg-high h-3/4" : "border-bg-mid h-1/4",
                    )}
                    style={{
                      left: (j / 4) * 100 + "%",
                    }}
                  ></div>
                )
              })}
            </div>
          )
        })}
      </div>
    ),
    [startTime, endTime, isToday ? now : 0],
  )
}

function VenuesSidebar(props: { model: CalendarModel; dayShown: string }) {
  const rows = props.model.dates[props.dayShown]?.rows

  const rowMotionProps: ComponentProps<typeof motion.div> = {
    initial: { height: 0, opacity: 0 },
    animate: { height: "auto", opacity: 1 },
    exit: { height: 0, opacity: 0 },
  }

  return (
    <div className="cal-sidebar flex flex-col absolute left-0 top-0 bg-bg w-browseCalendarSidebar z-[1] shadow-[1px_0_rgba(0,0,0,0.1)] md:w-[230px]">
      <AnimatePresence initial={false} mode="sync">
        {/* Venues shown on the currently active day */}
        {rows.map((venue, i) => {
          const rowStyles = venueRowStyles({ isSecondary: !!venue.hideVenueName })
          return (
            <motion.div key={venue.venueName + "_" + venue.cinemaName} {...rowMotionProps}>
              <div className={rowStyles.base()}>
                <p className={rowStyles.venueName()}>{venue.venueName}</p>
                {venue.cinemaName && <p className={rowStyles.cinemaName()}>{venue.cinemaName}</p>}
              </div>
            </motion.div>
          )
        })}
        {/* Stub rows */}
        {Array.from({ length: Math.max(0, props.model.maxRowsShown - rows.length) }).map((_, i) => {
          return (
            <motion.div key={"stub_" + i} {...rowMotionProps}>
              <div className={sessionRowStyles({ isSecondary: true }).base()}></div>
            </motion.div>
          )
        })}
      </AnimatePresence>
    </div>
  )
}

function CalendarDay(props: { model: CalendarModel; date: CalendarDate; active: boolean; isMobile: boolean }) {
  const { date } = props

  const now = useTime()
  const isToday = now.isSame(date.startHour, "day")

  return (
    <motion.div
      className={cn(
        "cal-day relative shrink-0 flex flex-col border-r border-r-bg-mid will-change-transform w-[calc(var(--pixels-per-minute)*var(--minutes-long))]",
      )}
      animate={{
        opacity: props.active ? 1 : 0.5,
      }}
      data-key={date.dateKey}
      style={
        {
          "--minutes-long": date.endHour.diff(date.startHour, "hours") * 60,
        } as any
      }
    >
      {/* Line indicating the current time */}
      {isToday && (
        <div
          className="absolute top-0 w-px h-full pointer-events-none"
          data-now-bar
          style={{ left: `calc(var(--pixels-per-minute)*${now.diff(date.startHour, "minute")})` }}
        >
          <div className="absolute inset-0 z-20 bg-coral opacity-10" />
          <div className="absolute inset-0 z-[5] bg-coral" />
        </div>
      )}
      {/* Each of the primary rows (venues) for this day */}
      <div className="flex flex-col relative">
        {date.rows.map((row, i) => {
          const rowStyles = sessionRowStyles({ isSecondary: !!row.hideVenueName || !props.active })
          return (
            <div className={rowStyles.base()} key={i}>
              {row.sessions.map((item) => {
                const hourOffset = dayjs(item.session?.info?.dateTime).diff(date.startHour, "minute")
                const duration = item.film.eventInfo?.runtime || 90
                // Hide films longer than 7 hours lol
                if (duration > 420) return null
                return (
                  <div
                    key={item.film.uri + "_" + item.session?.info?.dateTime}
                    className="absolute top-0 bottom-0 md:top-1 md:bottom-1 type-ui-item-sm cursor-pointer flex z-10"
                    style={{
                      left: `calc(var(--pixels-per-minute)*${hourOffset})`,
                      width: `calc(var(--pixels-per-minute)*${duration})`,
                    }}
                  >
                    <SessionTile item={item} isMobile={props.isMobile} />
                  </div>
                )
              })}
            </div>
          )
        })}

        {/* Stub rows */}
        {Array.from({ length: Math.max(0, props.model.maxRowsShown - date.rows.length) }).map((_, i) => {
          return <div key={i} className={sessionRowStyles({ isSecondary: true }).base()}></div>
        })}
      </div>
    </motion.div>
  )
}

function SessionTile(props: { item: FilmSearchResultItem; isMobile: boolean }) {
  const { film, session } = props.item

  return useMemo(() => {
    const contents = (
      <Link
        href={film.uri}
        className="flex bg-neutral-100 hover:bg-neutral-200 w-full md:rounded-sm md:overflow-hidden"
      >
        <div className="aspect-square h-full shrink-0">
          <ResponsiveImage {...film.media?.thumb!} aspect={1} />
        </div>
        <div className="w-full h-full flex gap-4 px-3">
          <div className="flex-1 flex flex-col justify-center gap-3 md:gap-1">
            <p className="type-ui-s md:type-ui-m line-clamp-1">{film.title}</p>
            <p className="hidden md:block type-ui-s whitespace-nowrap">
              {dayjs(session?.info?.dateTime).format("h:mma")} —{" "}
              {dayjs(session?.info?.dateTime)
                .add(film.eventInfo?.runtime || 90, "minute")
                .format("h:mma")}
            </p>
          </div>
          <div className="hidden md:flex flex-none items-center">
            {session?.info?.ferveId! && <WishlistIcon film={session?.info?.ferveId!} />}
          </div>
        </div>
      </Link>
    )

    if (props.isMobile) {
      return contents
    } else {
      return (
        <FilmHoverCard film={film} session={session}>
          {contents}
        </FilmHoverCard>
      )
    }
  }, [])
}

const baseRowStyles = tv({
  base: "h-[32px] md:h-9 border-t",
  variants: {
    isSecondary: {
      true: { base: "border-t-bg-low" },
      false: { base: "border-t-bg-high" },
    },
  },
  defaultVariants: {
    isSecondary: false,
  },
})

const venueRowStyles = tv({
  extend: baseRowStyles,
  base: "flex flex-col justify-center md:justify-between md:py-2 px-2",
  slots: {
    venueName: "type-body-s md:type-label-m transition-opacity opacity-1 text-fg",
    cinemaName: "type-label-caps md:type-label-caps text-fg-secondary",
  },
  variants: {
    isSecondary: {
      true: {
        venueName: "opacity-0",
      },
    },
  },
})

const sessionRowStyles = tv({
  extend: baseRowStyles,
  base: "w-full relative flex flex-col justify-between py-2 px-2 bg-bg-low/20",
  slots: {
    venueName: "type-label-m transition-opacity opacity-1 text-fg",
    cinemaName: "type-label-caps text-fg-secondary",
  },
  variants: {
    isSecondary: {
      true: {
        venueName: "opacity-0",
      },
    },
  },
})

function useTime() {
  const getTimeString = () => {
    return dayjs().format("YYYY-MM-DD HH:mm")
  }
  const [time, setTime] = useState(getTimeString())

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(getTimeString())
    }, 1000)

    return () => clearInterval(interval)
  }, [time])

  return useMemo(() => {
    return dayjs(time)
  }, [time])
}

function Clock() {
  const now = useTime()
  const indicator = useRef<HTMLSpanElement>(null)

  useEffect(() => {
    let flip = false
    const interval = setInterval(() => {
      flip = !flip
      indicator.current!.style.opacity = flip ? "0" : "1"
    }, 1000)

    return () => clearInterval(interval)
  }, [])

  return (
    <div className="type-ui-label whitespace-nowrap">
      {now.format("h")}
      <span ref={indicator}>:</span>
      {now.format("mma")}
    </div>
  )
}
